import createFetchClient, { Middleware } from 'openapi-fetch';

import { logoutSelectStatusCode } from './logoutSelectedStatusCodes';

import { getIdToken } from '@/services/auth';
import { actAsUserIdAtom, store, authenticatedUserIdAtom } from '@/services/globalState';
import type { paths } from '@primum/api-types';
import { isPendingOAuthUserId } from '@/util/authTokens';
import { env } from '@/util/env';
import { getAppVersion } from '@/util/getBuildInformation';
import { touchImpersonation } from '@/util/impersonationStorage';
import { parseRetryAfter } from '@/util/rateLimit';

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    public contentType: string | undefined,
    /**
     * Seconds the server asked the client to wait, from the `Retry-After`
     * header. Present on 429s so a surface can show a countdown rather than a
     * bare failure.
     */
    public retryAfterSeconds?: number
  ) {
    const bodyMessage =
      typeof body === 'object' &&
      body !== null &&
      'error' in body &&
      typeof (body as any).error === 'string'
        ? (body as any).error
        : undefined;

    super(bodyMessage ?? `API error ${status}`);

    this.name = 'ApiError';
  }
}

export const isApiErrorV2 = (
  error: unknown
): error is {
  contentType: string;
  body: {
    success: boolean;
    error: string;
  };
  status: number;
} => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'body' in error &&
    typeof (error as any).body === 'object' &&
    typeof (error as any).body.error === 'string'
  );
};

/**
 * A thin, typed wrapper around `openapi-fetch` that automatically handles:
 *
 * 1. Attaching the current Cognito ID token to every request via
 *    `Authorization: Bearer <token>` (only for authenticated users with DB records).
 *    Respects explicit Authorization headers set by callers (e.g., pending OAuth flows).
 * 2. Including an `App-Version` header when available so the back-end can log
 *    which build issued the request.
 * 3. Global response handling – any non-OK status is parsed according to the
 *    caller's `parseAs` option, mapped to our `isApiErrorV2` shape, and then
 *    re-thrown *after* running `logoutSelectStatusCode`, which performs forced
 *    logout on certain auth-related status codes (e.g. 401).
 *
 * Import this singleton rather than creating new fetch clients so that the
 * middleware executes consistently across the app:
 *
 * ```ts
 * import { fetchClient } from '@/util/fetchClient';
 * const { data, error } = await fetchClient('/v2/users/me').GET();
 * ```
 */
/**
 * An error body parsed as JSON when it is JSON, and kept as text when it is not.
 *
 * A gateway or CDN answers a timeout or outage with an HTML page. Parsing that
 * with `response.json()` throws a `SyntaxError` before any `ApiError` exists, so
 * the caller loses the status and a 504 reads as an unexplained failure.
 */
async function readJsonOrText(response: Response): Promise<unknown> {
  const text = await response.text();

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

const middleware: Middleware = {
  async onRequest({ request }) {
    // Middleware runs outside React context, so direct store access is required.
    // Using hooks (useAtomValue/useSetAtom) would violate React rules.
    const currentUserId = store.get(authenticatedUserIdAtom);

    const actAsUserId = store.get(actAsUserIdAtom);

    const isPending = isPendingOAuthUserId(currentUserId);

    const hasAuthHeader = request.headers.has('Authorization');

    if (!hasAuthHeader && currentUserId && !isPending) {
      const idToken = await getIdToken();

      if (idToken) {
        request.headers.set('Authorization', `Bearer ${idToken}`);
      }
    }

    if (actAsUserId) {
      request.headers.set('X-Act-As', actAsUserId);

      if (currentUserId) {
        // Fire-and-forget: refresh the persisted impersonation activity timestamp so
        // the entry survives a page reload until the user is genuinely idle.
        touchImpersonation(currentUserId, actAsUserId).catch(() => {});
      }
    }

    const appVersion = getAppVersion();

    if (appVersion) {
      request.headers.set('App-Version', appVersion);
    }

    return request;
  },

  async onResponse({ response, options }) {
    if (!response.ok) {
      const parseAs = options?.parseAs;

      const body = await (async () => {
        switch (parseAs) {
          case 'text':
            return response.text();
          case 'blob':
            return response.blob();
          case 'arrayBuffer':
            return response.arrayBuffer();
          case 'stream':
            return 'Unlike response.json(), this response body cannot be captured because a stream cannot be parsed.';
          case 'json':
          default:
            return readJsonOrText(response);
        }
      })();

      await logoutSelectStatusCode(response.status, response.url);

      throw new ApiError(response.status, body, parseAs, parseRetryAfter(response.headers));
    }

    // Returns nothing, which tells openapi-fetch to keep the response as it
    // came. Returning it instead fails every request on native: openapi-fetch
    // rejects anything that is not `instanceof Response`, and from SDK 56 the
    // global fetch is `expo/fetch`, whose response is its own class rather
    // than a subclass of the global `Response`.
    return undefined;
  },
};

export const fetchClient = createFetchClient<paths>({
  baseUrl: env.EXPO_PUBLIC_BASE_API_URL,
});

fetchClient.use(middleware);
